/* Conversion Tracker — browse funnel dataLayer for Yuki Art Prints.
 *
 * Phase D.2 of the conversion-tracking plan.
 *
 * Responsibilities:
 *   1. Own `window.dataLayer` initialization and event pushes for
 *      view_item / add_to_cart / view_cart / begin_checkout /
 *      remove_from_cart. Other theme scripts (wall-art-picker,
 *      cart-drawer, gallery-builder) call into this module to emit.
 *
 *   2. Provide the SHARED `buildItemId(designUuid, format, isSet, setUuid)`
 *      helper. Result MUST match Google Shopping feed `<g:id>` exactly
 *      so Klaviyo / Pinterest / Google Ads can join browse events to
 *      feed items.
 *
 *      FROZEN-CONTRACT ID FORMAT (mirrors):
 *      - darphane/electron/services/feed-item-generator.ts:197
 *      - darphane/electron/services/feed-item-generator-sets.ts:176
 *      - yuki-backend/src/services/conversion-dispatcher.ts (buildItemId)
 *      See yuki-backend/docs/conversion-id-format.md for the change
 *      procedure (versioned suffix + 24h Google re-crawl wait).
 *
 *   3. Expose a tiny public API on `window.YukiConversionTracker` so
 *      external scripts (klaviyo-tracker, pinterest-tracker, etc.)
 *      can subscribe via the standard dataLayer push pattern OR call
 *      the high-level helpers directly.
 *
 * NOT responsible for:
 *   - Reading product data from the picker / cart DOM (callers pass
 *     normalized objects to the helpers).
 *   - Sending events to Klaviyo / Pinterest / Google Ads (those are
 *     separate tracker bridges, loaded after this module).
 *   - Server-side conversion dispatch (Cloudflare Worker handles
 *     purchase events via the orders/create webhook).
 *
 * Load order in theme.liquid: this file MUST load BEFORE any tracker
 * bridges (klaviyo-tracker.js, pinterest-tracker.js) so they can hook
 * into the dataLayer at parse time and not miss the initial view_item
 * push that fires on DOMContentLoaded.
 */

(function () {
  if (typeof window === 'undefined') return;
  // Initialize dataLayer if no other script (e.g. GTM) has already done so.
  window.dataLayer = window.dataLayer || [];

  // ── Constants ──────────────────────────────────────────────────────
  const BRAND = 'Yuki Art Prints';
  const DEFAULT_CURRENCY = 'USD';
  const VALID_FORMATS = ['art_print', 'framed_art_print', 'canvas_print', 'framed_canvas_print'];
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /** Build an item_id matching the frozen Google Shopping feed format.
   *  Returns null if inputs are invalid (logs to console.warn so
   *  monitoring picks it up — silent failure here would mean
   *  conversion events with malformed IDs that don't join to the
   *  feed). */
  // Shortened format token used inside the frozen-contract item id — for BOTH
  // singles (`<uuid>_<token>`) and sets (`set_<uuid>_<token>`). Google's
  // `<g:id>` is documented at 50 chars but EMPIRICALLY enforced at ≤48, so
  // every format collapses to the same 2-3 char code (ap/fap/cp/fcp) rather
  // than the longer drop-`_print` scheme an earlier version used for singles.
  // MIRRORED — must stay byte-identical or browser ↔ feed ↔ conversion
  // attribution drifts:
  //   - darphane/electron/services/feed-item-generator.ts (shortFormatToken)
  //   - yuki-backend/src/services/conversion-dispatcher.ts (shortFormatToken)
  // Canonical spec: yuki-backend/docs/conversion-id-format.md
  function shortFormatToken(format) {
    switch (format) {
      case 'art_print':           return 'ap';
      case 'framed_art_print':    return 'fap';
      case 'canvas_print':        return 'cp';
      case 'framed_canvas_print': return 'fcp';
    }
    return null;
  }

  function buildItemId(designUuid, format, isSet, setUuid) {
    if (!format || VALID_FORMATS.indexOf(format) === -1) {
      console.warn('[conversion-tracker] invalid format:', format);
      return null;
    }
    if (isSet) {
      if (!setUuid || !UUID_RE.test(setUuid)) {
        console.warn('[conversion-tracker] invalid setUuid for Set item:', setUuid);
        return null;
      }
      return 'set_' + setUuid + '_' + shortFormatToken(format);
    }
    if (!designUuid || !UUID_RE.test(designUuid)) {
      console.warn('[conversion-tracker] invalid designUuid:', designUuid);
      return null;
    }
    return designUuid + '_' + shortFormatToken(format);
  }

  /** Normalize a Format property value (`"Framed Art Print"`) to the
   *  feed-token form (`"framed_art_print"`). Handles already-normalized
   *  values too. */
  function normalizeFormat(label) {
    if (!label) return null;
    const s = String(label).trim().toLowerCase().replace(/\s+/g, '_');
    return VALID_FORMATS.indexOf(s) >= 0 ? s : null;
  }

  /** Push a dataLayer event with the GA4 Enhanced Ecommerce envelope. */
  function pushEvent(name, value, items, extras) {
    const evt = Object.assign({
      event: name,
      ecommerce: Object.assign({
        currency: DEFAULT_CURRENCY,
        value: typeof value === 'number' ? value : 0,
        items: Array.isArray(items) ? items : [],
      }, extras || {}),
    });
    window.dataLayer.push(evt);
    // Mirror the same GA4 Enhanced Ecommerce event to GA4 directly (gtag), for
    // analytics + Google Ads remarketing audiences. Guarded: only fires when
    // the GA4 base tag (snippets/google-gtag.liquid) is present, never throws,
    // never blocks the dataLayer path. Conversions stay server-side — do NOT
    // add a purchase event here.
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, evt.ecommerce); } catch (e) { /* noop */ }
    }
    return evt;
  }

  // ── Event helpers ──────────────────────────────────────────────────

  /** Fire view_item from the product page. Caller passes the product
   *  context that's known at page load time (the default format if no
   *  picker change has happened yet). */
  function viewItem(args) {
    const item = buildItem(args);
    if (!item) return;
    return pushEvent('view_item', item.price, [item]);
  }

  /** Fire add_to_cart. `items` can be 1 item (single product picker)
   *  or many (Gallery Wall Builder bulk add, Wall Set add). The caller
   *  is responsible for assembling the items array (which includes
   *  the Set + its constituents for Wall Set adds). */
  function addToCart(args) {
    // Purchase-intent signal — ensure the captured click IDs are on the
    // cart before checkout (idempotent; session-guarded internally).
    try { writeAttributionToCart(); } catch (e) { /* never block the event */ }
    const items = (args.items || []).map(buildItem).filter(Boolean);
    if (items.length === 0) return;
    const value = items.reduce(function (sum, it) {
      return sum + (it.price || 0) * (it.quantity || 1);
    }, 0);
    return pushEvent('add_to_cart', value, items);
  }

  /** Fire remove_from_cart for a single line item. */
  function removeFromCart(args) {
    const item = buildItem(args);
    if (!item) return;
    return pushEvent('remove_from_cart', item.price * (item.quantity || 1), [item]);
  }

  /** Fire view_cart on cart drawer open OR cart page load. Reads from
   *  the rendered cart DOM (data-design-* + data-set-* attributes
   *  emitted by the cart Liquid templates in D.1.b). */
  function viewCart() {
    return pushCartLevelEvent('view_cart');
  }

  /** Fire begin_checkout when the customer clicks the cart's checkout
   *  button. Same item shape as view_cart. */
  function beginCheckout() {
    return pushCartLevelEvent('begin_checkout');
  }

  /** Shared implementation for view_cart + begin_checkout. Reads ALL
   *  rendered cart lines from the DOM. Skips lines without a UUID
   *  (e.g. legacy products before D.1 rollout, or non-shadow items
   *  like gift cards) — those won't have data-design-uuid. */
  function pushCartLevelEvent(eventName) {
    const lineEls = Array.from(document.querySelectorAll('[data-design-name]'));
    const items = lineEls.map(function (el) {
      return buildItem({
        item_id_override: null,
        design_uuid: el.dataset.designUuid || '',
        design_handle: el.dataset.designHandle || '',
        design_name: el.dataset.designName || '',
        design_image: el.dataset.designImage || '',
        format: el.dataset.designFormat || 'art_print',
        size: parseSize(el.dataset.designVariant) || null,
        frame_color: parseFrame(el.dataset.designVariant) || null,
        set_uuid: el.dataset.setUuid || null,
        set_id: el.dataset.setId || null,
        set_position: el.dataset.setPosition || null,
        quantity: parseInt(el.dataset.quantity || '1', 10) || 1,
        price: parseFloat(el.dataset.price || '0') || 0,
        category: 'Wall Art',
      });
    }).filter(Boolean);
    if (items.length === 0) return;
    const value = items.reduce(function (s, it) { return s + (it.price || 0) * (it.quantity || 1); }, 0);
    return pushEvent(eventName, value, items);
  }

  // ── Item construction ──────────────────────────────────────────────

  /** Build a single GA4-style item from a normalized args object.
   *  Returns null if the args don't have enough data to construct a
   *  valid item_id — that's a signal to skip the event silently
   *  rather than ship malformed data downstream. */
  function buildItem(args) {
    if (!args) return null;
    const format = args.format && VALID_FORMATS.indexOf(args.format) >= 0
      ? args.format
      : normalizeFormat(args.format) || 'art_print';
    const isSet = !!(args.set_uuid || args.is_set_item);
    const itemId = args.item_id_override
      || buildItemId(args.design_uuid, format, isSet, args.set_uuid);
    if (!itemId) return null;

    const variantBits = [];
    if (args.size) variantBits.push(args.size);
    if (args.frame_color && args.frame_color !== 'none') variantBits.push(args.frame_color);

    const item = {
      item_id: itemId,
      item_name: args.design_name || args.set_name || '',
      item_brand: BRAND,
      item_category: args.category || 'Wall Art',
      item_category2: args.subject || null,
      item_category3: formatLabel(format),
      item_variant: variantBits.length > 0 ? variantBits.join(' / ') : null,
      price: typeof args.price === 'number' ? args.price : 0,
      quantity: args.quantity || 1,
      currency: DEFAULT_CURRENCY,

      // Custom fields used by downstream trackers + the Worker:
      design_uuid: args.design_uuid || null,
      design_handle: args.design_handle || null,
      shopify_product_id: args.shopify_product_id || null,
      image_url: args.design_image || null,
      set_uuid: args.set_uuid || null,
      set_id: args.set_id || null,
      set_position: args.set_position || null,
      is_set_item: isSet,
    };
    return item;
  }

  function formatLabel(token) {
    switch (token) {
      case 'art_print': return 'Art Print';
      case 'framed_art_print': return 'Framed Art Print';
      case 'canvas_print': return 'Canvas Print';
      case 'framed_canvas_print': return 'Framed Canvas Print';
      default: return token;
    }
  }

  function parseSize(variantStr) {
    if (!variantStr) return null;
    const m = /(\d+x\d+)/.exec(variantStr);
    return m ? m[1] : null;
  }
  function parseFrame(variantStr) {
    if (!variantStr) return null;
    const lower = String(variantStr).toLowerCase();
    if (lower.indexOf('black') >= 0) return 'black';
    if (lower.indexOf('white') >= 0) return 'white';
    if (lower.indexOf('oak') >= 0) return 'natural_oak';
    return null;
  }

  // ── Logged-in customer identification ──────────────────────────────
  // Trackers (Klaviyo) need to know who the customer is for cross-device
  // event stitching. We push an `identify` event on init if the customer
  // bridge is present (set by layout/theme.liquid).
  function identifyFromShopify() {
    const c = window.Shopify && window.Shopify.customer;
    if (!c || !c.id || !c.email) return;
    window.dataLayer.push({
      event: 'identify',
      user: {
        user_id: String(c.id),
        email: c.email,
        first_name: c.first_name || null,
        last_name: c.last_name || null,
      },
    });
  }

  // ── Ad-attribution capture (Phase D.9 hardening) ───────────────────
  // Persist click IDs + UTMs into localStorage on FIRST landing so they
  // survive a multi-page journey to the Gallery Wall Builder. The builder
  // reads this key at save time and forwards it to the Worker — which is
  // otherwise limited to the Referer of the save request (= the builder
  // page URL, which no longer carries the original ?gclid= once the
  // visitor has navigated around the store).
  const ATTRIBUTION_KEY = 'yuki_attribution';
  const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90d ≈ Google click→conversion window
  const ATTRIBUTION_PARAMS = [
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'epik', 'pin_click_id',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  ];

  function readAttribution() {
    try {
      const raw = localStorage.getItem(ATTRIBUTION_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !obj.saved_at) return null;
      if (Date.now() - new Date(obj.saved_at).getTime() > ATTRIBUTION_MAX_AGE_MS) {
        localStorage.removeItem(ATTRIBUTION_KEY);
        return null;
      }
      return obj;
    } catch (e) {
      return null;
    }
  }

  function captureAttribution() {
    let params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch (e) {
      return;
    }
    const incoming = {};
    for (let i = 0; i < ATTRIBUTION_PARAMS.length; i++) {
      const k = ATTRIBUTION_PARAMS[i];
      const v = params.get(k);
      if (v && v.length > 0 && v.length < 500) incoming[k] = v;
    }
    if (Object.keys(incoming).length === 0) return; // nothing new on this page

    // Last-touch: a fresh ad click/UTM overwrites the stored value, but we
    // keep the EARLIEST landing_url for reference and stamp last_seen_url.
    const existing = readAttribution() || {};
    const merged = Object.assign({}, existing, incoming);
    const here = (window.location.pathname + window.location.search).slice(0, 1000);
    if (!merged.landing_url) merged.landing_url = here;
    merged.last_seen_url = here;
    merged.saved_at = new Date().toISOString();
    try {
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(merged));
    } catch (e) {
      /* private mode / quota — ignore */
    }
  }

  // ── Cart attribution write — gclid plumbing for NORMAL checkouts ────
  // Carries the captured click IDs into Shopify cart attributes so they
  // ride through to the order's note_attributes and on to the Worker →
  // Google Ads as a CLICK-ATTRIBUTED conversion. Mirrors how the Gallery
  // Wall flow writes `_wall_id` via /cart/update.js. Without this, normal
  // product checkouts drop the gclid and every conversion uploads
  // clickless (Enhanced-Conversions-for-Leads path), which is the root
  // cause of the persistent "turn on enhanced conversions for leads"
  // rejections.
  //
  // Coverage strategy: called BOTH proactively on load (so Set purchases
  // — which don't route through addToCart() — are still covered, since
  // the attribute lands on the cart before any checkout) AND from
  // addToCart() (purchase-intent signal). Session-guarded so an ad
  // visitor's pages don't each fire a /cart/update.js.
  const CART_ATTR_SESSION_KEY = 'yuki_cart_attr_written';

  function writeAttributionToCart() {
    const attr = readAttribution();
    if (!attr) return;
    const gclid = attr.gclid || '';
    const gbraid = attr.gbraid || '';
    const wbraid = attr.wbraid || '';
    // Only write when a click id is present. Organic visitors legitimately
    // carry none, and tagging their cart would be noise (and correctly
    // leaves their conversions clickless).
    if (!gclid && !gbraid && !wbraid) return;

    // Session guard keyed on the click-id set: write once per session, but
    // re-write if the click id changes (last-touch). Avoids a redundant
    // /cart/update.js on every page load.
    const sig = gclid + '|' + gbraid + '|' + wbraid;
    try {
      if (sessionStorage.getItem(CART_ATTR_SESSION_KEY) === sig) return;
    } catch (e) { /* private mode — proceed without the guard */ }

    // Only include keys that have a value, keeping the order's attributes
    // clean. `_`-prefix hides them from the customer-facing cart UI
    // (same convention as `_wall_id` / `_design_uuid`).
    const attributes = { _attribution_ts: attr.saved_at || '' };
    if (gclid) attributes._gclid = gclid;
    if (gbraid) attributes._gbraid = gbraid;
    if (wbraid) attributes._wbraid = wbraid;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function () {
      try { sessionStorage.setItem(CART_ATTR_SESSION_KEY, sig); } catch (e) { /* ignore */ }
    }).catch(function (err) {
      console.warn('[conversion-tracker] cart attribution write failed:', err);
    });
  }

  // ── Public API ─────────────────────────────────────────────────────
  window.YukiConversionTracker = {
    version: 1,
    buildItemId: buildItemId,
    normalizeFormat: normalizeFormat,
    formatLabel: formatLabel,
    viewItem: viewItem,
    addToCart: addToCart,
    removeFromCart: removeFromCart,
    viewCart: viewCart,
    beginCheckout: beginCheckout,
    // Exposed for debugging / manual re-trigger of the cart attribute write.
    writeAttributionToCart: writeAttributionToCart,
  };

  // Expose attribution to the gallery-builder bundle (separate script, same
  // origin → shares localStorage). The builder reads the key directly, but
  // this accessor is handy for debugging in the console.
  window.YukiAttribution = {
    key: ATTRIBUTION_KEY,
    get: readAttribution,
  };

  // ── Auto-bound browse-funnel handlers ──────────────────────────────
  // These two events fire from generic page/UI signals (cart page load,
  // checkout button click) and aren't tied to a specific custom element,
  // so we bind them here rather than asking every cart UI section to
  // wire its own integration.

  /** When the customer is on `/cart`, fire view_cart on initial paint.
   *  Cart-drawer view_cart is fired separately from cart-drawer.js's
   *  open() so we don't double-fire when the drawer auto-opens after
   *  /cart redirects. */
  function autoFireViewCartOnCartPage() {
    const path = (window.location.pathname || '').replace(/\/+$/, '');
    if (path !== '/cart' && path !== '/cart/') return;
    try {
      viewCart();
    } catch (err) {
      console.warn('[conversion-tracker] auto view_cart failed:', err);
    }
  }

  /** Delegated click handler for the cart's "Checkout" button. Catches
   *  both the cart drawer (`#CartDrawer-Checkout`, name="checkout") and
   *  the cart page (`#checkout`, name="checkout"). Dawn submits the cart
   *  form on click — we fire begin_checkout BEFORE that so the dataLayer
   *  push lands before the page navigation away to checkout.shopify.com. */
  function bindCheckoutClick() {
    document.addEventListener('click', function (ev) {
      const btn = ev.target && ev.target.closest && ev.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!btn) return;
      try {
        beginCheckout();
      } catch (err) {
        console.warn('[conversion-tracker] begin_checkout failed:', err);
      }
      // Do NOT preventDefault — let Dawn's form submission proceed.
    }, true);  // capture-phase so we fire BEFORE Dawn's submit handler
  }

  function bootBrowsers() {
    captureAttribution();
    // Proactively write click IDs to the cart on load. This is what
    // covers purchase paths that DON'T route through addToCart() (e.g.
    // the Wall Set bulk-add) — the attribute lands on the cart before
    // any checkout regardless of how items were added.
    writeAttributionToCart();
    identifyFromShopify();
    autoFireViewCartOnCartPage();
    bindCheckoutClick();
  }

  // Capture immediately too (don't wait for DOMContentLoaded) — the URL
  // params are available at parse time and we want the click ID stored
  // before any same-page navigation can strip it.
  captureAttribution();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootBrowsers);
  } else {
    bootBrowsers();
  }
})();
